import React, { useEffect, useRef } from 'react';

/**
 * BirthdayFireworks – Geburtstags-Animations-Overlay
 *
 * Zwei Modi:
 *  isSelf=false (default): Zeigt allen ANDEREN die Animation wenn ein Kollege Geburtstag hat
 *  isSelf=true:            Das Geburtstagskind selbst sieht eine persönliche Glückwunsch-Animation
 *
 * Phase 1 (0–2s): Raketen schießen von unten hoch → explodieren
 * Phase 2 (2–5s): Konfetti-Regen von oben
 * Auto-Close nach 5,5s oder bei Tippen
 */
export const BirthdayFireworks = ({ names, onClose, isSelf = false, age = null, daysSince = 0 }) => {
  const closedRef = useRef(false);

  const handleClose = () => {
    if (closedRef.current) return;
    closedRef.current = true;
    onClose();
  };

  useEffect(() => {
    let rainInterval = null;
    let autoTimer = null;

    const runAnimation = async () => {
      const { default: confetti } = await import('canvas-confetti');

      const COLORS = ['#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff922b', '#c77dff', '#ff61d2', '#fffb00'];

      // ── Phase 1: Raketen von unten ────────────────────────────────────────
      const rockets = [
        { x: 0.15, delay: 0 },
        { x: 0.5,  delay: 350 },
        { x: 0.85, delay: 700 },
        { x: 0.3,  delay: 1050 },
        { x: 0.7,  delay: 1400 },
        { x: 0.5,  delay: 1750 },
      ];

      rockets.forEach(({ x, delay }) => {
        setTimeout(() => {
          confetti({
            particleCount: 90,
            spread: 65,
            origin: { x, y: 1.05 },
            startVelocity: 75,
            gravity: 1.1,
            colors: COLORS,
            ticks: 220,
            scalar: 1.1,
          });
        }, delay);
      });

      // ── Phase 2: Konfetti-Regen von oben ─────────────────────────────────
      setTimeout(() => {
        rainInterval = setInterval(() => {
          confetti({
            particleCount: 5,
            angle: 265 + Math.random() * 10,
            spread: 120,
            origin: { x: Math.random(), y: -0.05 },
            colors: COLORS,
            gravity: 0.55,
            scalar: 1.2,
            drift: Math.random() * 0.6 - 0.3,
            ticks: 320,
          });
        }, 45);

        // Regen nach 3s stoppen
        setTimeout(() => {
          if (rainInterval) clearInterval(rainInterval);
        }, 3000);
      }, 1900);
    };

    runAnimation();

    // Auto-close nach 5,5s
    autoTimer = setTimeout(handleClose, 5500);

    return () => {
      closedRef.current = true;
      if (rainInterval) clearInterval(rainInterval);
      if (autoTimer) clearTimeout(autoTimer);
    };
  }, []); // onClose via ref, keine Deps nötig

  const displayNames = Array.isArray(names) ? names : [names];

  // isSelf: persönlicher Modus für das Geburtstagskind
  const selfName = displayNames[0] || '';
  const firstName = selfName.split(' ')[0] || selfName;
  const missedText = daysSince > 0
    ? `Herzlichen Glückwunsch zu deinem Geburtstag ${daysSince === 1 ? 'gestern' : `vor ${daysSince} Tagen`}!`
    : null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 9999, background: isSelf ? 'rgba(10,20,40,0.88)' : 'rgba(10,10,30,0.82)' }}
      onClick={handleClose}
      data-testid="birthday-fireworks-overlay"
    >
      <style>{`
        @keyframes bdPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        @keyframes bdGlow {
          0%, 100% { text-shadow: 0 0 20px rgba(255,215,0,0.7), 0 2px 6px rgba(0,0,0,0.6); }
          50% { text-shadow: 0 0 45px rgba(255,215,0,1), 0 0 15px rgba(255,140,0,0.9), 0 2px 6px rgba(0,0,0,0.6); }
        }
        @keyframes bdGlowSelf {
          0%, 100% { text-shadow: 0 0 24px rgba(100,200,255,0.8), 0 2px 6px rgba(0,0,0,0.6); }
          50% { text-shadow: 0 0 50px rgba(100,200,255,1), 0 0 20px rgba(50,150,255,0.9), 0 2px 6px rgba(0,0,0,0.6); }
        }
        @keyframes bdFadeIn {
          from { opacity: 0; transform: translateY(24px) scale(0.92); }
          to   { opacity: 1; transform: translateY(0)  scale(1); }
        }
        @keyframes bdCakeFloat {
          0%, 100% { transform: translateY(0) rotate(-3deg); }
          50%       { transform: translateY(-12px) rotate(3deg); }
        }
        @keyframes bdSubFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes bdAgePop {
          0% { transform: scale(0.5); opacity: 0; }
          60% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); }
        }
      `}</style>

      <div
        className="text-center px-6 max-w-lg w-full"
        onClick={e => e.stopPropagation()}
        style={{ animation: 'bdFadeIn 0.5s ease-out both' }}
      >
        {/* Kuchen-Icon */}
        <div style={{ fontSize: isSelf ? '80px' : '72px', lineHeight: 1, animation: 'bdCakeFloat 2s ease-in-out infinite', display: 'inline-block', marginBottom: '16px' }}>
          🎂
        </div>

        {isSelf ? (
          /* ── Persönlicher Modus: Das Geburtstagskind selbst ── */
          <>
            <p style={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: '13px', fontWeight: 700, letterSpacing: '3px',
              textTransform: 'uppercase', marginBottom: '8px',
              animation: 'bdSubFadeIn 0.6s 0.2s ease-out both',
            }}>
              {missedText || 'Heute ist dein besonderer Tag!'}
            </p>

            <h1 style={{
              color: '#7DD3FC',
              fontSize: '2.8rem', fontWeight: 900, lineHeight: 1.15, marginBottom: '4px',
              animation: 'bdGlowSelf 2s ease-in-out infinite, bdFadeIn 0.5s 0.15s ease-out both',
              letterSpacing: '-0.5px',
            }}>
              Alles Gute, {firstName}!
            </h1>

            {age && (
              <p style={{
                color: '#FFD700', fontSize: '1.5rem', fontWeight: 700, marginBottom: '4px',
                animation: 'bdAgePop 0.6s 0.5s ease-out both',
              }}>
                {age} Jahre jung
              </p>
            )}

            <p style={{
              color: 'rgba(255,255,255,0.65)', fontSize: '14px', marginBottom: '20px',
              animation: 'bdSubFadeIn 0.6s 0.7s ease-out both',
            }}>
              Das ganze Team wünscht dir einen wunderschönen Tag!
            </p>
          </>
        ) : (
          /* ── Team-Modus: Andere sehen die Animation ── */
          <>
            <p style={{
              color: 'rgba(255,255,255,0.75)',
              fontSize: '13px', fontWeight: 700, letterSpacing: '3px',
              textTransform: 'uppercase', marginBottom: '12px',
              animation: 'bdSubFadeIn 0.6s 0.2s ease-out both',
            }}>
              Alles Gute zum Geburtstag
            </p>

            {displayNames.map((name, i) => (
              <h1 key={i} style={{
                color: '#FFD700', fontSize: displayNames.length > 1 ? '2.2rem' : '3rem',
                fontWeight: 900, lineHeight: 1.15, marginBottom: '8px',
                animation: `bdGlow 2s ease-in-out infinite, bdFadeIn 0.5s ${0.15 + i * 0.1}s ease-out both`,
                letterSpacing: '-0.5px',
              }}>
                🎉 {name} 🎉
              </h1>
            ))}
          </>
        )}

        {/* Emoji-Leiste */}
        <p style={{
          fontSize: '28px', marginTop: '14px', letterSpacing: '6px',
          animation: 'bdPulse 1.4s ease-in-out infinite', display: 'block',
        }}>
          🥳 🎁 🎆 🎊 🎈
        </p>

        {/* Schließen-Hinweis */}
        <p style={{
          color: 'rgba(255,255,255,0.45)', fontSize: '12px',
          marginTop: '24px', letterSpacing: '1px',
          animation: 'bdSubFadeIn 0.6s 0.8s ease-out both',
        }}>
          Tippe um zu schließen
        </p>
      </div>
    </div>
  );
};

export default BirthdayFireworks;
